import {getProp, CanBeSignal, iterChildren} from '@gongxigames/framework/core/fragments/BuilderY';
import {Container as PixiContainer} from 'pixi.js';
import {ActionFunction, CallBack, runActions} from './Wrap';
import {Store, Signal} from '@gongxigames/framework/core/fragments/store/Store';
import {abortablePromise} from '@gongxigames/framework/lib/util/utilPromise';

export type TriggerFn = (trigger: CallBack) => TriggerUnsubscribeFn;
export type TriggerUnsubscribeFn = () => void;

export type TriggerProps<T> = {
    when?: CanBeSignal<T> | Promise<T>;
    equals?: CanBeSignal<T>;
    with?: CanBeSignal<TriggerFn>;
    actions: ActionFunction[];
};

function triggerByPromise<T extends PixiContainer>(val: Promise<any>, trigger: CallBack, instance: T): void {
    const abortController = new AbortController();

    instance.on('destroyed', () => abortController.abort());
    abortablePromise(val, abortController.signal).then(trigger);
}

function triggerByPromiseAndCondition<T extends PixiContainer>(val: Promise<any>, equals: CanBeSignal<T>, trigger: CallBack, instance: T): void {
    const abortController = new AbortController();

    instance.on('destroyed', () => abortController.abort());
    abortablePromise(val, abortController.signal)
        .then(val => {
            const equalsVal = getProp(equals);

            if (val === equalsVal) {
                trigger();
            }
        });
}

function triggerBySignalChange<T extends PixiContainer>(val: Signal<any>, trigger: CallBack, instance: T): void {
    const off = Store.effect(() => {
        val.value;

        trigger();
    });

    instance.on('destroyed', off);
}

function triggerBySignalAndCondition<T extends PixiContainer>(val: Signal<any>, equals: CanBeSignal<T>, trigger: CallBack, instance: T): void {
    const off = Store.effect(() => {
        const equalsVal = getProp(equals);

        if (val.value === equalsVal) {
            trigger();
        }
    });

    instance.on('destroyed', off);
}

function triggerByFunction<T extends PixiContainer>(withFn: CanBeSignal<TriggerFn>, trigger: CallBack, instance: T): void {
    const triggerFn = getProp(withFn);
    const off = triggerFn(trigger);

    instance.on('destroyed', off);
}

export function Trigger<T>(props: TriggerProps<T>): ActionFunction {
    const result: ActionFunction = (instance, done) => {
        const hasEquals = Object.prototype.hasOwnProperty.call(props, 'equals');
        const hasWhen = Object.prototype.hasOwnProperty.call(props, 'when');
        const hasWith = Object.prototype.hasOwnProperty.call(props, 'with');
        const whenIsPromise = Object.prototype.hasOwnProperty.call(props, 'when');

        if (hasEquals && !hasWhen) {
            throw new Error('Cant check equals without value');
        }

        const trigger = () => {
            const iterator = iterChildren<ActionFunction>(props.actions);
            const actions = [...iterator];

            runActions(instance, actions);
        };

        switch (true) {
            case hasEquals && whenIsPromise:
                triggerByPromiseAndCondition(props.when as Promise<any>, props.equals as CanBeSignal<any>, trigger, instance);
                break;

            case whenIsPromise:
                triggerByPromise(props.when as Promise<any>, trigger, instance);
                break;

            case !whenIsPromise && !hasEquals:
                triggerBySignalChange(props.when as Signal<any>, trigger, instance);
                break;

            case !whenIsPromise && hasEquals:
                triggerBySignalAndCondition(props.when as Signal<any>, props.equals as CanBeSignal<any>, trigger, instance);
                break;

            case hasWith:
                triggerByFunction(props.with! as Signal<any>, trigger, instance);
                break;

            default:
                throw new Error('Trigger props is incorrect');
        }

        done();
    };

    return result;
};
